import { type ReactNode } from 'react';
import { Skeleton } from '@patternfly/react-core';
import { Tbody, Tr, Td, ExpandableRowContent } from '@patternfly/react-table';
import { type Row } from '@tanstack/react-table';
import { type VirtualItem } from '@tanstack/react-virtual';
import { TableRow } from '~/shared/components/TableV2/TableRow';

/** Props for the {@link TableBody} component. */
interface TableBodyProps<TData> {
  /** All rows from the table model. */
  rows: Row<TData>[];
  /** Virtual items from the virtualizer — only these are rendered in the DOM. */
  virtualRows: VirtualItem[];
  /** Total pixel height of all rows (used for spacer calculation). */
  totalSize: number;
  /** Callback from virtualizer to measure a row element for dynamic height. */
  measureElement: (el: HTMLElement | null) => void;
  /** Returns a unique, stable string ID for a given row. */
  getRowId: (row: TData) => string;
  /** Whether rows are expandable. */
  enableExpansion?: boolean;
  /** Whether rows are selectable. */
  enableRowSelection?: boolean;
  /** Render function for expanded row content. */
  expandedContent?: (row: TData) => ReactNode;
  /** Number of visible columns, used for `colSpan` on expanded content and loading rows. */
  visibleColumnCount: number;
  /** Whether the next page is being fetched. Shows skeleton rows when `true`. */
  isFetchingNextPage?: boolean;
  /** Scroll margin from the virtualizer, subtracted from spacer calculations. */
  scrollMargin?: number;
}

/**
 * Renders the virtualized table body.
 *
 * Only rows visible in the viewport (plus overscan) are rendered. Top and
 * bottom spacer `<Tr>` elements maintain correct scroll height. When a row
 * is expanded, its expanded content is rendered in a full-width row below it.
 *
 * Shows skeleton loading rows when `isFetchingNextPage` is `true`.
 *
 * @typeParam TData - The row data type
 */
export const TableBody = <TData,>({
  rows,
  virtualRows,
  totalSize,
  measureElement,
  getRowId,
  enableExpansion,
  enableRowSelection,
  expandedContent,
  visibleColumnCount,
  isFetchingNextPage,
  scrollMargin = 0,
}: TableBodyProps<TData>) => {
  const lastVirtualRow = virtualRows[virtualRows.length - 1];
  const bottomSpacerHeight = lastVirtualRow
    ? totalSize - (lastVirtualRow.start + lastVirtualRow.size)
    : 0;

  const topSpacerHeight = virtualRows.length > 0 ? virtualRows[0].start - scrollMargin : 0;
  const leadingCellCount = Number(Boolean(enableExpansion)) + Number(Boolean(enableRowSelection));
  const expandedContentColumnCount = visibleColumnCount - leadingCellCount;

  return (
    // Multiple <Tbody> siblings (not nested) — each becomes a direct child of the
    // <table> element. Multiple <tbody> per <table> is valid HTML5 (same pattern
    // used by ConformaGroupedTable). Per-row wrappers let TanStack Virtual's
    // measureElement capture the combined height of the main row + expanded content
    // as a single virtual item, preventing spacer miscalculations on expand/collapse.
    <>
      <Tbody data-test="table-body" style={{ overflowAnchor: 'none' }}>
        {topSpacerHeight > 0 && <Tr style={{ height: topSpacerHeight }} />}
      </Tbody>
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) return null;
        const rowId = getRowId(row.original);
        const isExpanded = Boolean(enableExpansion && row.getIsExpanded() && expandedContent);

        return (
          // No explicit role needed: <Tbody> renders a native <tbody> whose implicit
          // ARIA role is "rowgroup", correctly grouping the main row with its optional
          // expanded content for screen readers.
          <Tbody
            key={rowId}
            ref={measureElement}
            data-index={virtualRow.index}
            isExpanded={isExpanded}
            style={{ overflowAnchor: 'none' }}
          >
            <TableRow
              row={row}
              rowId={rowId}
              virtualIndex={virtualRow.index}
              enableExpansion={enableExpansion}
              isContentExpanded={isExpanded}
              enableRowSelection={enableRowSelection}
            />
            {isExpanded && (
              <Tr isExpanded>
                {enableRowSelection && <Td />}
                {enableExpansion && <Td />}
                <Td colSpan={expandedContentColumnCount}>
                  <ExpandableRowContent>{expandedContent(row.original)}</ExpandableRowContent>
                </Td>
              </Tr>
            )}
            {enableExpansion && (
              <Tr isBorderRow aria-hidden="true">
                <Td noPadding colSpan={visibleColumnCount} />
              </Tr>
            )}
          </Tbody>
        );
      })}
      <Tbody style={{ overflowAnchor: 'none' }}>
        {bottomSpacerHeight > 0 && <Tr style={{ height: bottomSpacerHeight }} />}
        {isFetchingNextPage &&
          Array.from({ length: 3 }, (_, rowIdx) => (
            <Tr key={`skeleton-${rowIdx}`} data-test="table-loading-more">
              {Array.from({ length: visibleColumnCount }, (__, colIdx) => (
                <Td key={colIdx}>
                  <Skeleton width={`${50 + ((rowIdx + colIdx) % 4) * 10}%`} />
                </Td>
              ))}
            </Tr>
          ))}
      </Tbody>
    </>
  );
};
